// React import for JSX components
import ReactMarkdown from "react-markdown";
import { supabase } from "./supabase";

const SB = "https://mhixfakpafenionfrpto.supabase.co/rest/v1";
// Quota MESSAGES/mois par formule — grille juin 2026 (source backend :
// api/_lib/plans.js ; catalogue d'achat : src/lib/plansCatalog.js).
// Les clés legacy restent mappées pour les comptes existants.
const PLANS = {
  trial: 50,
  essentiel: 500,
  cabinet: 1500,
  cabinet_plus: 5000,
  secretariat: 400,
  collaborateur: 500,
  individuel: 1200,
};
const PLAN_LABELS = {
  trial: "Essai gratuit",
  essentiel: "Essentiel",
  cabinet: "Cabinet",
  cabinet_plus: "Cabinet+",
  secretariat: "Secrétariat",
  collaborateur: "Collaborateur",
  individuel: "Individuel",
  enterprise: "Enterprise",
};

// Anon key resolved at module load time (Vite injects via import.meta.env)
const _ANON = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

// Auth-aware fetch helpers — read fresh session on each call to avoid race
// conditions with useEffect-driven header initialization. Falls back to
// anon-key requests when no session is present (RLS will then filter).
let _sbHeaders = { apikey: _ANON, Authorization: `Bearer ${_ANON}` };

async function _resolveAuthHeaders() {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const tok = session?.access_token || _ANON;
    return { apikey: _ANON, Authorization: `Bearer ${tok}` };
  } catch {
    return { apikey: _ANON, Authorization: `Bearer ${_ANON}` };
  }
}

// En-têtes pour les endpoints serverless /api/* protégés (drive/…) : ils
// exigent le JWT avocat (le serveur dérive le lawyer du token, le lawyer_id
// du body est ignoré). On lit la session courante avant chaque appel.
// Renvoie null si pas de session — l'appelant doit alors signaler la
// déconnexion. NB : différent de _resolveAuthHeaders ci-dessus (REST Supabase,
// apikey + repli anon) — ici AUCUN repli anonyme : sans session, pas d'appel.
async function authHeaders() {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) return null;
  return {
    "Content-Type": "application/json",
    "Authorization": `Bearer ${session.access_token}`,
  };
}

const sbGet = async (p) => { try { const headers = await _resolveAuthHeaders(); const r = await fetch(`${SB}${p}`, { headers }); return r.ok ? r.json() : []; } catch { return []; } };
const sbCount = async (p) => { try { const headers = await _resolveAuthHeaders(); const r = await fetch(`${SB}${p}`, { headers: { ...headers, Prefer: 'count=exact', Range: '0-0' } }); const range = r.headers.get('content-range'); return range ? parseInt(range.split('/')[1], 10) : 0; } catch { return 0; } };
const sbUpsert = async (table, data) => { try { const headers = await _resolveAuthHeaders(); const r = await fetch(`${SB}/${table}`, { method: "POST", headers: { ...headers, "Content-Type": "application/json", "Prefer": "resolution=merge-duplicates,return=minimal" }, body: JSON.stringify(Array.isArray(data) ? data : [data]) }); return r.ok; } catch { return false; } };

// Endpoint d'agrégation — un seul appel pour le mount du dashboard.
// Audit 28/04/2026 : remplace 8 requêtes REST en parallèle par 1 fetch.
async function bootstrapDashboard() {
  const { data: { session } } = await supabase.auth.getSession();
  const tok = session?.access_token;
  if (!tok) throw new Error('Not authenticated');
  const r = await fetch('/api/dashboard/bootstrap', {
    headers: { Authorization: `Bearer ${tok}` },
  });
  if (!r.ok) throw new Error(`bootstrap ${r.status}`);
  return r.json();
}

// Google OAuth
const GOOGLE_CLIENT_ID = "1086967010791-g1tde1ilk7k74udo42j8n681ejtepv4v.apps.googleusercontent.com";
const GOOGLE_REDIRECT_URI = "https://app.iavocat.ai/api/auth/google/callback";
// gmail.modify inclut la lecture des messages → gmail.readonly serait redondant
// (retiré volontairement pour alléger la revue CASA, cf. docs/oauth/verification-justifications.md)
const GOOGLE_SCOPES = "https://www.googleapis.com/auth/gmail.send https://www.googleapis.com/auth/gmail.modify https://www.googleapis.com/auth/calendar.events https://www.googleapis.com/auth/drive.readonly";
const GOOGLE_AUTH_URL = "https://accounts.google.com/o/oauth2/v2/auth";

function startGoogleOAuth(lawyerId) {
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: GOOGLE_REDIRECT_URI,
    response_type: "code",
    scope: GOOGLE_SCOPES,
    access_type: "offline",
    prompt: "consent",
    state: lawyerId,
  });
  window.location.href = GOOGLE_AUTH_URL + "?" + params.toString();
}

// Microsoft OAuth (server-side callback, same pattern as Google)
const MS_CLIENT_ID = "921cd659-62ee-46e9-ab8b-bf6938e9af08";
const MS_REDIRECT_URI = "https://app.iavocat.ai/api/auth/microsoft/callback";
const MS_SCOPES = "openid profile offline_access User.Read Calendars.ReadWrite Mail.ReadWrite Mail.Send Files.Read.All";
const MS_AUTH_URL = "https://login.microsoftonline.com/common/oauth2/v2.0/authorize";

// Dropbox OAuth PKCE
const DBX_APP_KEY = ""; // TODO: Set after creating Dropbox app at https://www.dropbox.com/developers/apps
const DBX_REDIRECT_URI = "https://app.iavocat.ai";
const DBX_AUTH_URL = "https://www.dropbox.com/oauth2/authorize";
const DBX_TOKEN_URL = "https://api.dropboxapi.com/oauth2/token";

async function generatePKCE() {
  const arr = new Uint8Array(32);
  crypto.getRandomValues(arr);
  const verifier = btoa(String.fromCharCode(...arr)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  const challenge = btoa(String.fromCharCode(...new Uint8Array(digest))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  return { verifier, challenge };
}

// Microsoft OAuth — server-side callback (same pattern as Google)
// No more PKCE client-side — tokens are exchanged and encrypted server-side
function startMicrosoftOAuth(lawyerId) {
  const params = new URLSearchParams({
    client_id: MS_CLIENT_ID,
    response_type: "code",
    redirect_uri: MS_REDIRECT_URI,
    scope: MS_SCOPES,
    response_mode: "query",
    prompt: "consent",
    state: lawyerId, // lawyer_id passed as state (same as Google)
  });
  window.location.href = MS_AUTH_URL + "?" + params.toString();
}

async function handleDropboxCallback(code) {
  const verifier = sessionStorage.getItem("dbx_pkce_verifier");
  if (!verifier) return { error: "No PKCE verifier found" };
  try {
    const resp = await fetch(DBX_TOKEN_URL, {
      method: "POST", headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        client_id: DBX_APP_KEY, code, redirect_uri: DBX_REDIRECT_URI,
        grant_type: "authorization_code", code_verifier: verifier
      })
    });
    const tokens = await resp.json();
    if (tokens.error) return { error: tokens.error_description || tokens.error };
    const email = tokens.account_id || "unknown";
    // Get Dropbox account info
    let displayName = email;
    try {
      const acct = await (await fetch("https://api.dropboxapi.com/2/users/get_current_account", {
        method: "POST", headers: { Authorization: "Bearer " + tokens.access_token }
      })).json();
      if (acct.email) displayName = acct.email;
    } catch(e) {}
    sessionStorage.removeItem("dbx_pkce_verifier");
    sessionStorage.removeItem("dbx_lawyer_id");
    return { success: true, email: displayName, tokens };
  } catch (e) { return { error: e.message }; }
}

// === DESIGN TOKENS (light/dark) ===
// Tous les tokens pointent désormais sur des CSS variables (cf. index.css).
// Le switch light/dark est géré au niveau CSS via [data-theme="dark"] sur
// <html>. Comme les valeurs sont des var(--xxx), un changement de thème
// est immédiatement répercuté dans tous les composants qui consomment T,
// sans aucun re-render React.
//
// LIGHT et DARK sont identiques (le proxy renvoie le même objet) — la
// distinction se fait au niveau CSS, pas JS.
const TOKENS = {
  // Brand (gold == bordeaux dans le système v3)
  navy:    "var(--text-primary)",
  gold:    "var(--accent)",
  goldL:   "var(--accent-deep)",
  goldBg:  "var(--accent-soft)",
  goldBd:  "var(--accent-soft)",

  // Surfaces
  bg:      "var(--bg-page)",
  wh:      "var(--bg-card)",
  bd:      "var(--line)",
  bdL:     "var(--line)",

  // Text
  tx:      "var(--text-primary)",
  txM:     "var(--text-muted)",
  txL:     "var(--text-faint)",
  txXL:    "var(--text-faint)",

  // Status
  red:     "var(--status-miss)",
  redBg:   "var(--status-miss-soft)",
  redBd:   "var(--status-miss-line)",
  orange:  "var(--status-warn)",
  orangeBg:"var(--status-warn-soft)",
  orangeBd:"var(--status-warn-line)",
  blue:    "var(--info)",
  blueBg:  "var(--info-soft)",
  blueBd:  "var(--info-line)",
  green:   "var(--status-ok)",
  greenBg: "var(--status-ok-soft)",
  greenBd: "var(--status-ok-line)",
  purple:  "#7C3AED",

  serif: "'Spectral', Georgia, serif",
  sans: "'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif",
  mono: "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace",
  r: 10, rS: 6, rL: 14,
};
const LIGHT = TOKENS;
const DARK = TOKENS;
// Global theme state — will be set by App component
let _darkMode = false;
const T = new Proxy({}, { get: (_, prop) => (_darkMode ? DARK : LIGHT)[prop] });
const card = (extra = {}) => ({ background: T.wh, borderRadius: T.r, border: `1px solid ${T.bd}`, ...extra });

// === MARKDOWN RENDERER ===
const mdComponents = (isUser) => ({
  h1: ({ children }) => <div style={{ fontSize: "1.05rem", fontWeight: 700, color: isUser ? "white" : T.navy, marginTop: 12, marginBottom: 6, fontFamily: T.serif }}>{children}</div>,
  h2: ({ children }) => <div style={{ fontSize: "0.95rem", fontWeight: 700, color: isUser ? "rgba(255,255,255,0.9)" : T.navy, marginTop: 10, marginBottom: 5, fontFamily: T.serif, borderBottom: isUser ? "none" : `1px solid ${T.bdL}`, paddingBottom: 4 }}>{children}</div>,
  h3: ({ children }) => <div style={{ fontSize: "0.88rem", fontWeight: 700, color: isUser ? "rgba(255,255,255,0.9)" : T.navy, marginTop: 8, marginBottom: 4 }}>{children}</div>,
  p: ({ children }) => <div style={{ marginBottom: 8, lineHeight: 1.65 }}>{children}</div>,
  strong: ({ children }) => <strong style={{ fontWeight: 700, color: isUser ? "white" : T.navy }}>{children}</strong>,
  em: ({ children }) => <em style={{ color: isUser ? "rgba(255,255,255,0.7)" : T.txM }}>{children}</em>,
  ul: ({ children }) => <ul style={{ paddingLeft: 18, marginBottom: 8 }}>{children}</ul>,
  ol: ({ children }) => <ol style={{ paddingLeft: 18, marginBottom: 8 }}>{children}</ol>,
  li: ({ children }) => <li style={{ marginBottom: 3, lineHeight: 1.55 }}>{children}</li>,
  hr: () => <hr style={{ border: "none", borderTop: `1px solid ${isUser ? "rgba(255,255,255,0.15)" : T.bdL}`, margin: "10px 0" }} />,
  code: ({ inline, children }) => inline
    ? <code style={{ background: isUser ? "rgba(255,255,255,0.12)" : T.bg, padding: "1px 5px", borderRadius: 4, fontSize: "0.82em", fontFamily: "monospace", color: isUser ? "#FFD700" : T.gold }}>{children}</code>
    : <pre style={{ background: isUser ? "rgba(0,0,0,0.2)" : T.bg, padding: "10px 12px", borderRadius: 8, fontSize: "0.78rem", fontFamily: "monospace", overflowX: "auto", marginBottom: 8, border: `1px solid ${isUser ? "transparent" : T.bdL}`, lineHeight: 1.5 }}><code>{children}</code></pre>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: isUser ? "#FFD700" : T.gold, textDecoration: "underline" }}>{children}</a>,
  blockquote: ({ children }) => <blockquote style={{ borderLeft: `3px solid ${isUser ? "rgba(255,255,255,0.3)" : T.gold}`, paddingLeft: 12, margin: "8px 0", color: isUser ? "rgba(255,255,255,0.7)" : T.txM, fontStyle: "italic" }}>{children}</blockquote>,
});
const MdMsg = ({ content, isUser = false, small = false }) => (
  <div style={{ fontSize: small ? "0.8rem" : "0.88rem", lineHeight: small ? 1.55 : 1.65 }}>
    <ReactMarkdown components={mdComponents(isUser)}>{content}</ReactMarkdown>
  </div>
);

// === ICONS ===
const P = (ch) => <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{ch}</svg>;
const Ic = {
  home: P(<><path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>),
  folder: P(<path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/>),
  cal: P(<><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></>),
  mail: P(<><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22 6 12 13 2 6"/></>),
  gear: P(<><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></>),
  chat: P(<path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>),
  send: P(<><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></>),
  file: P(<><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></>),
  x: P(<><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></>),
  minus: P(<line x1="5" y1="12" x2="19" y2="12"/>),
  alert: P(<><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></>),
  chev: P(<polyline points="9 18 15 12 9 6"/>),
  team: P(<><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/></>),
};

const mc = { penal: { bg: "#FEF3C7", tx: "#92400E" }, famille: { bg: "#DBEAFE", tx: "#1E40AF" }, corporel: { bg: "#D1FAE5", tx: "#065F46" }, civil: { bg: "#F3E8FF", tx: "#6B21A8" }, travail: { bg: "#FFE4E6", tx: "#9F1239" }, admin: { bg: "#E0F2FE", tx: "#075985" }, etrangers: { bg: "#FEF9C3", tx: "#854D0E" }, immobilier: { bg: "#ECFDF5", tx: "#047857" }, affaires: { bg: "#F1F5F9", tx: "#334155" }, contrats: { bg: "#FFF7ED", tx: "#9A3412" } };

function classifyEvent(summary) {
  if (!summary) return { category: "autre", color: "#94a3b8", icon: "📅", label: "Autre" };
  const s = summary.toLowerCase();
  if (s.includes("audience") || s.includes("comparution") || s.includes("jugement") || s.includes("assises") || s.includes("correctionnelle") || s.includes("tribunal"))
    return { category: "audience", color: "#DC2626", icon: "", label: "Audience" };
  if (s.includes("réunion") || s.includes("rendez-vous") || s.includes("rdv") || s.includes("consultation") || s.includes("entretien"))
    return { category: "rdv", color: "#2563EB", icon: "👤", label: "Rendez-vous" };
  if (s.includes("expertise") || s.includes("expert"))
    return { category: "expertise", color: "#7C3AED", icon: "🔬", label: "Expertise" };
  if (s.includes("médiation") || s.includes("conciliation"))
    return { category: "mediation", color: "#059669", icon: "🤝", label: "Médiation" };
  if (s.includes("délai") || s.includes("deadline") || s.includes("échéance") || s.includes("date limite"))
    return { category: "deadline", color: "#D97706", icon: "⏰", label: "Échéance" };
  if (s.includes("garde à vue") || s.includes("gav") || s.includes("défèrement"))
    return { category: "urgence", color: "#BE123C", icon: "🚨", label: "Urgence" };
  if (s.includes("formation") || s.includes("conférence") || s.includes("séminaire") || s.includes("colloque"))
    return { category: "formation", color: "#0891B2", icon: "📚", label: "Formation" };
  if (s.includes("personnel") || s.includes("perso") || s.includes("privé"))
    return { category: "personnel", color: "#6B7280", icon: "🏠", label: "Personnel" };
  if (s.includes("instruction") || s.includes("juge") || s.includes("jld") || s.includes("chambre"))
    return { category: "audience", color: "#DC2626", icon: "", label: "Audience" };
  if (s.includes("client") || s.includes("dossier") || s.includes("cabinet"))
    return { category: "rdv", color: "#2563EB", icon: "👤", label: "Rendez-vous" };
  return { category: "autre", color: "#94a3b8", icon: "📅", label: "Autre" };
}


// ─── Mutable state accessors ───
export function syncSbHeaders(h) { _sbHeaders = h; }
export function getSbHeaders() { return _sbHeaders; }
export function syncDarkMode(v) { _darkMode = v; }
export function getDarkMode() { return _darkMode; }

export { SB, PLANS, PLAN_LABELS, _sbHeaders, sbGet, sbCount, sbUpsert, authHeaders, bootstrapDashboard, LIGHT, DARK, T, _darkMode, card, mdComponents, MdMsg, P, Ic, mc };
export { GOOGLE_CLIENT_ID, GOOGLE_REDIRECT_URI, GOOGLE_SCOPES, GOOGLE_AUTH_URL };
export { MS_CLIENT_ID, MS_REDIRECT_URI, MS_SCOPES, MS_AUTH_URL };
export { DBX_APP_KEY, DBX_REDIRECT_URI, DBX_AUTH_URL, DBX_TOKEN_URL };
export { startGoogleOAuth, generatePKCE, startMicrosoftOAuth, handleDropboxCallback };
export { classifyEvent };
